import { useCached3DObjectIfExists } from "@/object-cache";
import {
  PositionedPointCloud,
  PositionedPointCloudProps,
} from "@/registration-tools/common/rendering/positioned-point-cloud";
import { useAppSelector } from "@/store/store-hooks";
import { RevisionScanEntity } from "@faro-lotv/service-wires";
import { useEffect, useMemo, type JSX } from "react";
import { Matrix4 } from "three";
import { selectRevisionEntityWorldTransformCache } from "../store/data-preparation-ui/data-preparation-ui-selectors";
import { selectIsEntitySetToVisible } from "../store/data-preparation-ui/data-preparation-ui-visibility-selectors";
import { selectPointCloudStreamForScanEntity } from "../store/revision/revision-scan-selectors";
import { ScanEntityPointCloudWireframe } from "./debug/scan-entity-point-cloud-wireframe";
import { invalidateSceneEvent } from "./revision-rendering-pipeline";

type CaptureTreePointCloudProps = Pick<
  PositionedPointCloudProps,
  "onInitialPositioning"
> & {
  /** The scan entity to render. */
  scanEntity: RevisionScanEntity;
};

/** @returns The capture tree point cloud rendered at the position as determined in the revision. */
export function RevisionScanRenderer({
  scanEntity,
  onInitialPositioning,
}: CaptureTreePointCloudProps): JSX.Element | null {
  const pointCloudStream = useAppSelector(
    selectPointCloudStreamForScanEntity(scanEntity),
  );
  const pointCloudObject = useCached3DObjectIfExists(
    pointCloudStream,
    "RevisionScanRenderer",
  );
  const cachedWorldTransform = useAppSelector(
    selectRevisionEntityWorldTransformCache(scanEntity.id),
  );
  const transform = useMemo(
    () => new Matrix4().fromArray(cachedWorldTransform.worldMatrix),
    [cachedWorldTransform],
  );

  const isVisible = useAppSelector(selectIsEntitySetToVisible(scanEntity.id));

  useEffect(() => {
    // Refresh scene on visibility toggle
    invalidateSceneEvent.emit();
  }, [isVisible]);

  if (!pointCloudObject) {
    return null;
  }

  return (
    <>
      <PositionedPointCloud
        pointCloudObject={pointCloudObject}
        transform={transform}
        onInitialPositioning={onInitialPositioning}
        isVisible={isVisible}
      />
      <ScanEntityPointCloudWireframe
        scanEntityId={scanEntity.id}
        pointCloudObject={pointCloudObject}
        transform={transform}
        isVisible={isVisible}
      />
    </>
  );
}
